// 「移动到…」下拉 —— 收件箱批量工具条与邮件详情页共用。
// 用原生 <select>：手机上直接弹系统选择器，不用自己处理浮层定位/点外关闭。
// 选中即触发移动，然后复位回占位项（它是动作按钮，不是表单字段）。

import { FolderInput } from 'lucide-react';

import { useFolders } from '../../lib/folders.js';

interface Props {
  /**
   * 邮件当前所在：null = 收件箱；数字 = 该文件夹。当前位置不出现在选项里。
   * undefined = 位置不统一（如搜索结果跨文件夹混选）→ 收件箱与全部文件夹都列出。
   */
  current?: number | null | undefined;
  onMove: (folderId: number | null) => void;
  disabled?: boolean;
  /** sm = 批量工具条（h-7）；md = 详情页操作区（h-9） */
  size?: 'sm' | 'md';
}

const INBOX_VALUE = 'inbox';

export function MoveToFolderSelect({ current, onMove, disabled, size = 'sm' }: Props) {
  const q = useFolders();
  const folders = (q.data?.items ?? []).filter((f) => f.id !== current);
  const noTargets = folders.length === 0 && current === null;

  return (
    <label
      className={
        (size === 'md' ? 'h-9 ' : 'h-7 ') +
        'relative inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800 whitespace-nowrap ' +
        (disabled || noTargets ? 'opacity-50' : 'cursor-pointer')
      }
    >
      <FolderInput className="h-3.5 w-3.5" />
      {/* 详情页工具栏（md）窄屏只留图标；批量工具条（sm）文字常显 */}
      <span className={size === 'md' ? 'hidden sm:inline' : undefined}>
        {noTargets ? '暂无文件夹' : '移动到…'}
      </span>
      <select
        value=""
        disabled={disabled || noTargets}
        onChange={(e) => {
          const v = e.target.value;
          if (!v) return;
          onMove(v === INBOX_VALUE ? null : Number(v));
        }}
        aria-label="移动到文件夹"
        className="absolute inset-0 cursor-pointer opacity-0 disabled:cursor-not-allowed"
      >
        <option value="" disabled>
          移动到…
        </option>
        {current !== null ? <option value={INBOX_VALUE}>收件箱</option> : null}
        {folders.map((f) => (
          <option key={f.id} value={String(f.id)}>
            {f.name}
          </option>
        ))}
      </select>
    </label>
  );
}
